/* eslint-disable no-console */
import { UserPromiseClient } from '@trustero/trustero-api-web/lib/account/account_grpc_web_pb'
import {
  UpdateUser,
  UpdateUserAccessRequest,
} from '@trustero/trustero-api-web/lib/account/account_pb'
import { AuthorizationPromiseClient } from '@trustero/trustero-api-web/lib/authz/authz_grpc_web_pb'
import {
  AccessRole,
  GetAccessRolesRequest,
  GetAccessRolesResponse,
  CreateAccessRoleRequest,
  UpdateAccessRoleRequest,
  DeleteAccessRoleRequest,
  BASE_ACCESS_ROLE,
  DomainRoles,
} from '@trustero/trustero-api-web/lib/authz/authz_pb'
import { Permissions } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { StringList } from '@trustero/trustero-api-web/lib/common/collections_pb'
import { UserGroupServicePromiseClient } from '@trustero/trustero-api-web/lib/usergroup/usergroup_grpc_web_pb'
import { UpdateUserGroupMembershipsRequest } from '@trustero/trustero-api-web/lib/usergroup/usergroup_pb'
import log from 'loglevel'
import { useCallback } from 'react'
import {
  useAuthorizedGrpcClient,
  useAuthorizedGrpcClientWithContentUpdate,
} from 'src/adapter/grpcClient'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useUsers } from 'src/components/async/Users/useUsers'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { showInfoToast } from 'src/Utils/helpers/toast'
import { useGrpcRevalidateByMethod } from 'src/components/async/useGrpcMutate'
import { useUserGroups } from './userGroups/userGroups.hooks'

export const useUpdateUserGroups = (): ((
  userId: string,
  groupIds: string[],
) => Promise<void>) => {
  const { mutate } = useUserGroups()
  const revalidateByMethod = useGrpcRevalidateByMethod()
  const userGroupClient = useAuthorizedGrpcClientWithContentUpdate(
    UserGroupServicePromiseClient,
  )

  return useCallback(
    async (userId: string, groupIds: string[]) => {
      const request = new UpdateUserGroupMembershipsRequest()
        .setUserId(userId)
        .setGroupIdsList(groupIds)
      try {
        await userGroupClient.updateUserGroupMemberships(request)
        await mutate()
        await revalidateByMethod(UserGroupServicePromiseClient.prototype.list)
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to update user groups')
      }
    },
    [userGroupClient, mutate, revalidateByMethod],
  )
}

export const useAccessRoles = (
  includeNoAccess?: boolean,
  shouldFetch = true,
): GrpcResponse<GetAccessRolesResponse> => {
  const request = new GetAccessRolesRequest()
  if (includeNoAccess !== undefined) {
    request.setIncludeNoAccess(includeNoAccess)
  }
  const { response } = useSwrImmutableGrpc(
    AuthorizationPromiseClient.prototype.getAccessRoles,
    request,
    shouldFetch,
  )
  return NewGrpcResponse(response)
}

export const useUpdateUserAccess = (): ((
  accessRoleId: string,
  userId: string,
  auditIds?: string[],
) => Promise<void>) => {
  const { mutate } = useUsers()
  const userClient = useAuthorizedGrpcClientWithContentUpdate(UserPromiseClient)
  return useCallback(
    async (accessRoleId: string, userId: string, auditIds?: string[]) => {
      try {
        const request = new UpdateUserAccessRequest()
          .setUserId(userId)
          .setAccessRoleId(accessRoleId)
        if (auditIds) {
          request.setAuditIds(new StringList().setItemList(auditIds))
        }
        await userClient.updateUserAccess(request)
        await mutate()
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to update user access')
      }
    },
    [userClient, mutate],
  )
}

export const useUpdateUserName = (): ((
  name: string,
  userId: string,
) => Promise<boolean>) => {
  const { mutate } = useUsers()
  const userClient = useAuthorizedGrpcClientWithContentUpdate(UserPromiseClient)

  return useCallback(
    async (name: string, userId: string) => {
      const request = new UpdateUser().setName(name).setId(userId)
      try {
        await userClient.update(request)
        await mutate()
        return true
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to update user name')
        return false
      }
    },
    [mutate, userClient],
  )
}

export const useUpdateUserDepartment = (): ((
  departmentId: string,
  userId: string,
) => Promise<boolean>) => {
  const { mutate } = useUsers()
  const userClient = useAuthorizedGrpcClientWithContentUpdate(UserPromiseClient)

  return useCallback(
    async (departmentId: string, userId: string) => {
      const request = new UpdateUser().setDepartment(departmentId).setId(userId)
      try {
        await userClient.update(request)
        await mutate()
        return true
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to update user department')
        return false
      }
    },
    [mutate, userClient],
  )
}

// Resolves to the created role so callers can navigate to its show page. Existing
// callers ignore the return value.
export const useCreateAccessRole = (): ((
  name: string,
  baseAccessRole: BASE_ACCESS_ROLE,
  domainOverrides?: DomainRoles,
  permissionOverrides?: Permissions,
) => Promise<AccessRole>) => {
  const { mutate } = useUsers()
  const { mutate: mutateAccessRoles } = useAccessRoles()
  const revalidateByMethod = useGrpcRevalidateByMethod()
  const authzClient = useAuthorizedGrpcClient(AuthorizationPromiseClient)

  return useCallback(
    async (
      name: string,
      baseAccessRole: BASE_ACCESS_ROLE,
      domainOverrides?: DomainRoles,
      permissionOverrides?: Permissions,
    ) => {
      const request = new CreateAccessRoleRequest()
        .setName(name)
        .setBaseAccessRole(baseAccessRole)

      if (domainOverrides) {
        request.setDomainOverrides(domainOverrides)
      }
      if (permissionOverrides) {
        request.setPermissionOverrides(permissionOverrides)
      }

      try {
        const created = await authzClient.createAccessRole(request)
        await mutate()
        await mutateAccessRoles()
        await revalidateByMethod(
          AuthorizationPromiseClient.prototype.getAccessRoles,
        )
        return created
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to create user role')
        throw error
      }
    },
    [mutate, mutateAccessRoles, authzClient, revalidateByMethod],
  )
}

export const useDeleteAccessRole = (): ((id: string) => Promise<boolean>) => {
  const { mutate } = useUsers()
  const { mutate: mutateAccessRoles } = useAccessRoles()
  const revalidateByMethod = useGrpcRevalidateByMethod()
  const authzClient = useAuthorizedGrpcClient(AuthorizationPromiseClient)

  return useCallback(
    async (id: string) => {
      const request = new DeleteAccessRoleRequest().setId(id)

      try {
        await authzClient.deleteAccessRole(request)
        await mutate()
        await mutateAccessRoles()
        await revalidateByMethod(
          AuthorizationPromiseClient.prototype.getAccessRoles,
        )
        return true
      } catch (error) {
        log.error(error)
        return false
      }
    },
    [mutate, mutateAccessRoles, authzClient, revalidateByMethod],
  )
}

export const useUpdateAccessRole = (): ((
  id: string,
  name: string,
  domainOverrides?: DomainRoles,
  permissionOverrides?: Permissions,
) => Promise<void>) => {
  const { mutate } = useUsers()
  const { mutate: mutateAccessRoles } = useAccessRoles()
  const revalidateByMethod = useGrpcRevalidateByMethod()
  const authzClient = useAuthorizedGrpcClient(AuthorizationPromiseClient)

  return useCallback(
    async (
      id: string,
      name: string,
      domainOverrides?: DomainRoles,
      permissionOverrides?: Permissions,
    ) => {
      const request = new UpdateAccessRoleRequest().setId(id).setName(name)

      if (domainOverrides) {
        // Callers here build the role's complete domain snapshot, so ask the server to
        // replace rather than merge - merging could never clear a domain back to its
        // base role default, which is what "Reset to Defaults" does.
        request.setDomainOverrides(domainOverrides)
        request.setReplaceDomainOverrides(true)
      }
      if (permissionOverrides) {
        request.setPermissionOverrides(permissionOverrides)
      }

      try {
        await authzClient.updateAccessRole(request)
        await mutate()
        await mutateAccessRoles()
        await revalidateByMethod(
          AuthorizationPromiseClient.prototype.getAccessRoles,
        )
      } catch (error) {
        log.error(error)
        showInfoToast('Failed to update user role')
        throw error
      }
    },
    [mutate, mutateAccessRoles, authzClient, revalidateByMethod],
  )
}
